import _ from "lodash";
import {
  CAMPAIGN_MAX_PICKS,
  CAMPAIGN_WINDOW_DEFAULT_MONTHS,
  CAMPAIGN_WINDOW_MAX_MONTHS,
  CAMPAIGN_WINDOW_MIN_MONTHS,
  campaignCriteriaOfStored,
  CampaignCriteria,
  toStoredCampaign,
} from "encharge-domain/lib/helpers/salesforce/campaignSync";
import {
  CAMPAIGN_SIDE,
  isFiniteNumberText,
  LEGACY_CONDITIONS,
  LIMITS,
  OPPORTUNITY_SIDE,
  OPPORTUNITY_SIDE_WINDOW,
  opportunityOffOf,
  opportunityWindowOf,
  readOpportunitySide,
  readSide,
  SaveProblem,
  SideDraft,
  SideReading,
  StoredSyncSide,
  SyncCondition,
  SyncObject,
  SyncRule,
  takesCommaList,
  takesDate,
  takesNoValue,
  takesValueList,
  toStored,
  toStoredOpportunity,
  validateForSave,
} from "encharge-domain/lib/helpers/salesforceSyncCriteria";
import { formatWhen } from "components/Sync/Salesforce/format";
import {
  CRITERIA_CONFLICT_CODE,
  CRITERIA_INVALID_CODE,
  SalesforceCriteriaProblem,
  SalesforceCriteriaRequestError,
} from "store/persistence/persistSalesforceCriteria";
import { PANEL_COPY } from "./salesforcePanel.copy";

/**
 * What the Sync criteria screen decides, with no React in it: the rules a
 * customer is editing, the shape they are saved in, whether they changed, what
 * is wrong with a rule, and what the screen says when a save is refused. Pure
 * functions, so every rule can be tested on its own.
 *
 * The rules of an object are groups joined by OR, the rules of a group joined
 * by AND, and one Keep out list (the criteria design, 1.1). They are stored as
 * the old tab stored them (`filterProperties`, one list) while they are flat, and
 * with a sibling key `rules` once they use groups, Keep out or a new condition
 * (2.1, 2.2). The domain helper (`encharge-domain/lib/helpers/salesforceSyncCriteria`)
 * is the one reading of the stored value that the API and this screen share:
 * `readSide` reads a stored side, `toStored` writes one, `validateForSave` is
 * the save check.
 *
 * A side the customer did not change is saved exactly as it loaded, byte for
 * byte (2.2): a rule keeps the stored rule it was read from (`loaded`) until a
 * field of it is edited, and `toStored` hands the loaded side back when the
 * rules read the same.
 */

/**
 * The sides of the stored rules the screen edits. Lead and Contact choose which
 * people come in. Opportunity (Lane 1 of the Opportunity sync) chooses which
 * Opportunities Encharge keeps up to date: it has the same groups and Keep out
 * list, a switch and a window in months, and is stored as
 * `{ off?, windowMonths?, rules? }`, never as a flat list. Campaign (CMP1)
 * chooses which Campaigns Encharge copies: the same groups and Keep out list, a
 * switch that is OFF until the customer chooses, a window, the two tick boxes and
 * the Campaigns picked by hand; stored as
 * `{ off?, windowMonths?, includeActive?, includeRecent?, picks?, rules? }`.
 */
export type PeopleSide = SyncObject;
export type CriteriaSide =
  | PeopleSide
  | typeof OPPORTUNITY_SIDE
  | typeof CAMPAIGN_SIDE;

/** The people sides, in the order the screen draws them. Opportunity and Campaign are drawn apart. */
export const CRITERIA_SIDES: PeopleSide[] = ["Lead", "Contact"];

/** Every side the draft holds, the people first. */
export const ALL_SIDES: CriteriaSide[] = [
  ...CRITERIA_SIDES,
  OPPORTUNITY_SIDE,
  CAMPAIGN_SIDE,
];

export const isOpportunitySide = (side: CriteriaSide): boolean =>
  side === OPPORTUNITY_SIDE;

export const isCampaignSide = (side: CriteriaSide): boolean =>
  side === CAMPAIGN_SIDE;

/** "Leads", "Contacts", "Opportunities", "Campaigns": the plural of a side's name. */
export const pluralOfSide = (side: CriteriaSide): string =>
  side === OPPORTUNITY_SIDE ? "Opportunities" : `${side}s`;

/** The fourteen conditions of the old tab, in the words and the order the service's schema has them. */
export const CONDITIONS: string[] = [...LEGACY_CONDITIONS];

export const DEFAULT_CONDITION = "is";

/** Everything but these two needs a value: the rule is about whether the field has one. */
export const takesValue = (condition: string): boolean =>
  !takesNoValue(condition);

/** is one of / is not one of: one string, the values separated by commas. */
export const takesList = (condition: string): boolean =>
  takesCommaList(condition);

const takesNumber = (condition: string): boolean =>
  condition === "is more than" || condition === "is less than";

// ---------------------------------------------------------------------------
// The draft

/** What a customer edits in a rule. */
export type RuleFields = {
  propertyName: string;
  /** The word that is stored (`is one of` for the label "is any of" on a text field) */
  condition: string;
  /** The value of a condition that takes one value, and the comma list of is one of / is not one of */
  propertyValue: string;
  /** The values of is any of / is not any of on a multi-select field */
  propertyValues: string[];
};

/**
 * A rule as it is being edited. `key` only tells the rows apart; it is never
 * saved. `loaded` is the stored rule this one was read from, with the fields it
 * read as: a rule whose fields are still those is saved as the stored rule, and
 * is the kind of rule whose problems show from the moment the screen opens
 * (6.7). A rule added on the screen has none.
 */
export type DraftRule = RuleFields & {
  key: number;
  loaded?: { rule: unknown; fields: RuleFields };
};

/** A group: its rules are joined by AND. */
export type DraftGroup = { key: number; rules: DraftRule[] };

/**
 * One object: groups joined by OR, and the Keep out list (any one keeps a
 * record out). `listed` is set once the customer has added or taken away a rule
 * here: an object that ends with no rules is then saved with an empty list, as
 * the old tab's form saved a section that was touched (the "Listed" rule of the
 * criteria design, 2.2). It is not a change of the rules.
 */
export type DraftSide = {
  groups: DraftGroup[];
  keepOut: DraftRule[];
  listed: boolean;
};

/** What the Opportunity card holds beside its rules: the switch and the window in months. */
export type OpportunityOptions = { off: boolean; windowMonths: number };

/**
 * What the Campaigns card holds beside its rules (CMP1): the switch, the window in
 * months, the two tick boxes and the Campaigns picked by hand. `off` is true until
 * the customer switches Campaigns on (the stored side reads `off: false`).
 */
export type CampaignOptions = {
  off: boolean;
  windowMonths: number;
  includeActive: boolean;
  includeRecent: boolean;
  /** 18 character Campaign ids, each once, in the order they were picked */
  picks: string[];
};

export type CriteriaDraft = Record<CriteriaSide, DraftSide> & {
  opportunity: OpportunityOptions;
  campaign: CampaignOptions;
};

export const CAMPAIGN_WINDOWS: number[] = Array.from(
  { length: CAMPAIGN_WINDOW_MAX_MONTHS - CAMPAIGN_WINDOW_MIN_MONTHS + 1 },
  (_unused, n) => CAMPAIGN_WINDOW_MIN_MONTHS + n
);

export const OPPORTUNITY_WINDOWS: number[] = Array.from(
  {
    length: OPPORTUNITY_SIDE_WINDOW.max - OPPORTUNITY_SIDE_WINDOW.min + 1,
  },
  (_unused, n) => OPPORTUNITY_SIDE_WINDOW.min + n
);

const isObject = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asText = (value: unknown): string =>
  typeof value === "string" ? value : value == null ? "" : String(value);

const fieldsOf = (rule: DraftRule): RuleFields => ({
  propertyName: rule.propertyName,
  condition: rule.condition,
  propertyValue: rule.propertyValue,
  propertyValues: rule.propertyValues,
});

/** A rule added on the screen: no field, the first condition, no value. */
export const newRule = (newKey: () => number): DraftRule => ({
  key: newKey(),
  propertyName: "",
  condition: DEFAULT_CONDITION,
  propertyValue: "",
  propertyValues: [],
});

const ruleFromStored = (stored: unknown, newKey: () => number): DraftRule => {
  const rule = isObject(stored) ? stored : {};
  const fields: RuleFields = {
    propertyName: asText(rule.propertyName),
    // a rule with no condition is shown with none, not with "is": the customer
    // has to choose one, and "is" would look chosen already
    condition: asText(rule.condition),
    propertyValue: asText(rule.propertyValue),
    propertyValues: Array.isArray(rule.propertyValues)
      ? rule.propertyValues.filter(
          (item: unknown): item is string => typeof item === "string"
        )
      : [],
  };
  return { key: newKey(), ...fields, loaded: { rule: stored, fields } };
};

/**
 * The rules the account has stored, ready to edit: `readSide` reads each object
 * (a flat list is one group, `rules` is its groups and Keep out, nothing is no
 * groups). `stored` is the whole `syncCriteria` value, or what a restore puts
 * back.
 */
export const draftFromStored = (
  stored: unknown,
  newKey: () => number
): CriteriaDraft => {
  const sideOf = (reading: SideReading): DraftSide => ({
    groups: reading.groups.map((rules) => ({
      key: newKey(),
      rules: rules.map((rule) => ruleFromStored(rule, newKey)),
    })),
    keepOut: reading.keepOut.map((rule) => ruleFromStored(rule, newKey)),
    listed: false,
  });
  const campaign = campaignCriteriaOfStored(stored);
  return {
    Lead: sideOf(readSide(stored, "Lead")),
    Contact: sideOf(readSide(stored, "Contact")),
    Opportunity: sideOf(readOpportunitySide(stored)),
    Campaign: sideOf(campaignReadingOf(campaign)),
    opportunity: {
      off: opportunityOffOf(stored),
      windowMonths: opportunityWindowOf(stored),
    },
    campaign: campaignOptionsOf(campaign),
  };
};

/** The Campaign rules as the groups and Keep out the screen draws (the reader already read them). */
const campaignReadingOf = (campaign: CampaignCriteria): SideReading => ({
  source:
    campaign.groups.length > 0 || campaign.keepOut.length > 0
      ? "rules"
      : "none",
  groups: campaign.groups,
  keepOut: campaign.keepOut,
  ...(campaign.refused ? { refused: campaign.refused } : {}),
});

const campaignOptionsOf = (campaign: CampaignCriteria): CampaignOptions => ({
  off: campaign.off,
  windowMonths: campaign.windowMonths,
  includeActive: campaign.includeActive,
  includeRecent: campaign.includeRecent,
  picks: [...campaign.picks],
});

/** Whether a rule is still exactly what was read from the stored value. */
export const isUntouched = (rule: DraftRule): boolean =>
  rule.loaded !== undefined && _.isEqual(fieldsOf(rule), rule.loaded.fields);

/**
 * One rule as it is saved: the stored rule it was read from while it is
 * untouched, else what its fields say (a value only for a condition that takes
 * one, a list only for the list words of a multi-select field).
 */
const syncRuleOf = (rule: DraftRule): SyncRule => {
  if (rule.loaded && isUntouched(rule)) return rule.loaded.rule as SyncRule;
  const base = {
    propertyName: rule.propertyName,
    condition: rule.condition as SyncCondition,
  };
  if (takesNoValue(rule.condition)) return base;
  if (takesValueList(rule.condition)) {
    return { ...base, propertyValues: rule.propertyValues };
  }
  return { ...base, propertyValue: rule.propertyValue };
};

const sideDraftOf = (side: DraftSide): SideDraft => ({
  groups: side.groups.map((group) => group.rules.map(syncRuleOf)),
  keepOut: side.keepOut.map(syncRuleOf),
});

const baseSideOf = (base: unknown, object: PeopleSide): unknown =>
  isObject(base) ? base[object] : undefined;

/**
 * One object as it is saved: as `toStored` writes it, and, for an object the
 * customer touched that ends with no rules and had no list, an empty list.
 */
const sideToSave = (side: DraftSide, base: unknown): StoredSyncSide => {
  const stored = toStored(sideDraftOf(side), base);
  if (
    side.listed &&
    stored.rules === undefined &&
    stored.filterProperties === undefined
  ) {
    return { filterProperties: [] };
  }
  return stored;
};

/**
 * The Opportunity side as it is saved (toStoredOpportunity): the side exactly as
 * it loaded while the draft chooses the same, else `{ off?, windowMonths?, rules? }`.
 * An account whose stored rules have no Opportunity side and whose draft says
 * nothing for it has none in the value: the key is left out, so a save of Leads
 * and Contacts alone sends what it always sent.
 */
const opportunityToSave = (
  draft: CriteriaDraft,
  base: unknown
): Record<string, unknown> | undefined => {
  const saved = toStoredOpportunity(
    {
      ...sideDraftOf(draft.Opportunity),
      off: draft.opportunity.off,
      windowMonths: draft.opportunity.windowMonths,
    },
    base
  );
  const hadSide = isObject(base) && base[OPPORTUNITY_SIDE] !== undefined;
  return !hadSide && Object.keys(saved).length === 0 ? undefined : saved;
};

/**
 * The Campaign side as it is saved (toStoredCampaign): the side exactly as it
 * loaded while the draft chooses the same, else `{ off?, windowMonths?,
 * includeActive?, includeRecent?, picks?, rules? }`. An account whose stored rules
 * have no Campaign side and whose draft says nothing for it has none in the value:
 * the key is left out, so a save of Leads and Contacts alone sends what it always
 * sent.
 */
const campaignToSave = (
  draft: CriteriaDraft,
  base: unknown
): Record<string, unknown> | undefined => {
  const saved = toStoredCampaign(
    {
      ...sideDraftOf(draft.Campaign),
      ...draft.campaign,
      picks: [...draft.campaign.picks],
    },
    base
  );
  const hadSide = isObject(base) && base[CAMPAIGN_SIDE] !== undefined;
  return !hadSide && Object.keys(saved).length === 0 ? undefined : saved;
};

/** The value to send: Lead and Contact, and the Opportunity and Campaign sides when there are some (see criteriaToSave). */
export type CriteriaToSave = {
  Lead: StoredSyncSide;
  Contact: StoredSyncSide;
  Opportunity?: Record<string, unknown>;
  Campaign?: Record<string, unknown>;
};

/**
 * The value to send: each object as `toStored` writes it (2.2). `base` is the
 * value the draft was read from, the stored rules, or on a restore the rules
 * being put back: an object whose rules are as that value reads is saved as that
 * value holds it, byte for byte; a flat object is saved as the old tab saved it
 * (`{ filterProperties }`, no `rules`, and `{}` for one that never had a list);
 * a grouped one gets `rules`, and the `filterProperties` beside it are copied
 * through from `base`. The Opportunity side is written by toStoredOpportunity.
 */
export const criteriaToSave = (
  draft: CriteriaDraft,
  base: unknown
): CriteriaToSave => {
  const opportunity = opportunityToSave(draft, base);
  const campaign = campaignToSave(draft, base);
  return {
    Lead: sideToSave(draft.Lead, baseSideOf(base, "Lead")),
    Contact: sideToSave(draft.Contact, baseSideOf(base, "Contact")),
    ...(opportunity === undefined ? {} : { Opportunity: opportunity }),
    ...(campaign === undefined ? {} : { Campaign: campaign }),
  };
};

/** Whether one object's rules differ from the ones it started with (for Opportunity, its switch and window too). */
export const sideChanged = (
  draft: CriteriaDraft,
  baseline: CriteriaDraft,
  object: CriteriaSide
): boolean =>
  !_.isEqual(sideDraftOf(draft[object]), sideDraftOf(baseline[object])) ||
  (object === OPPORTUNITY_SIDE &&
    !_.isEqual(draft.opportunity, baseline.opportunity)) ||
  (object === CAMPAIGN_SIDE &&
    !sameCampaignOptions(draft.campaign, baseline.campaign));

/** The Campaign options are the same when they choose the same Campaigns: picks compared as a set. */
const sameCampaignOptions = (a: CampaignOptions, b: CampaignOptions): boolean =>
  a.off === b.off &&
  a.windowMonths === b.windowMonths &&
  a.includeActive === b.includeActive &&
  a.includeRecent === b.includeRecent &&
  a.picks.length === b.picks.length &&
  a.picks.every((pick) => b.picks.includes(pick));

/** Whether the rules on screen differ from the ones the customer started with. */
export const criteriaChanged = (
  draft: CriteriaDraft,
  baseline: CriteriaDraft
): boolean => ALL_SIDES.some((side) => sideChanged(draft, baseline, side));

// ---------------------------------------------------------------------------
// Editing

/** Where a new rule goes: a group (its position in the list), or Keep out. */
export type RuleTarget = { group: number } | "keepOut";

/** Switches Opportunity sync on or off. Its rules and window stay as they are. */
export const setOpportunityOff = (
  draft: CriteriaDraft,
  off: boolean
): CriteriaDraft => ({
  ...draft,
  opportunity: { ...draft.opportunity, off },
});

/** Chooses the window in months (a whole number from 1 to 12; anything else is ignored). */
export const setOpportunityWindow = (
  draft: CriteriaDraft,
  windowMonths: number
): CriteriaDraft =>
  OPPORTUNITY_WINDOWS.includes(windowMonths)
    ? { ...draft, opportunity: { ...draft.opportunity, windowMonths } }
    : draft;

/** Switches Campaign sync on or off. Its choice and rules stay as they are. */
export const setCampaignOff = (
  draft: CriteriaDraft,
  off: boolean
): CriteriaDraft => ({
  ...draft,
  campaign: { ...draft.campaign, off },
});

/** Chooses the window in months (a whole number from 1 to 12; anything else is ignored). */
export const setCampaignWindow = (
  draft: CriteriaDraft,
  windowMonths: number
): CriteriaDraft =>
  CAMPAIGN_WINDOWS.includes(windowMonths)
    ? { ...draft, campaign: { ...draft.campaign, windowMonths } }
    : draft;

/** Ticks or unticks "Campaigns that are Active" (includeActive) or "Campaigns that started or ended in the last N months" (includeRecent). */
export const setCampaignBox = (
  draft: CriteriaDraft,
  box: "includeActive" | "includeRecent",
  ticked: boolean
): CriteriaDraft => ({
  ...draft,
  campaign: { ...draft.campaign, [box]: ticked },
});

/** Picks one more Campaign by hand (its 18 character id); a Campaign already picked, and more than the most, are ignored. */
export const addCampaignPick = (
  draft: CriteriaDraft,
  campaignId: string
): CriteriaDraft =>
  draft.campaign.picks.includes(campaignId) ||
  draft.campaign.picks.length >= CAMPAIGN_MAX_PICKS
    ? draft
    : {
        ...draft,
        campaign: {
          ...draft.campaign,
          picks: [...draft.campaign.picks, campaignId],
        },
      };

/** Takes a Campaign off the picks. */
export const removeCampaignPick = (
  draft: CriteriaDraft,
  campaignId: string
): CriteriaDraft => ({
  ...draft,
  campaign: {
    ...draft.campaign,
    picks: draft.campaign.picks.filter((pick) => pick !== campaignId),
  },
});

/** Nothing is chosen to read: no box ticked and no Campaign picked ("Choose the Campaigns to copy."). */
export const campaignChoiceIsEmpty = (draft: CriteriaDraft): boolean =>
  !draft.campaign.includeActive &&
  !draft.campaign.includeRecent &&
  draft.campaign.picks.length === 0;

/** The default window, for the screen to say what an unchosen window means. */
export const CAMPAIGN_DEFAULT_WINDOW = CAMPAIGN_WINDOW_DEFAULT_MONTHS;

const withSide = (
  draft: CriteriaDraft,
  object: CriteriaSide,
  change: (side: DraftSide) => DraftSide
): CriteriaDraft => ({ ...draft, [object]: change(draft[object]) });

/** A new rule at the end of a group (the first group is made when there is none) or of Keep out. */
export const addRule = (
  draft: CriteriaDraft,
  object: CriteriaSide,
  target: RuleTarget,
  newKey: () => number
): CriteriaDraft =>
  withSide(draft, object, (side) => {
    if (target === "keepOut") {
      return {
        ...side,
        keepOut: [...side.keepOut, newRule(newKey)],
        listed: true,
      };
    }
    if (side.groups.length === 0) {
      return {
        ...side,
        groups: [{ key: newKey(), rules: [newRule(newKey)] }],
        listed: true,
      };
    }
    return {
      ...side,
      listed: true,
      groups: side.groups.map((group, index) =>
        index === target.group
          ? { ...group, rules: [...group.rules, newRule(newKey)] }
          : group
      ),
    };
  });

/** "Add an OR group": a new group with one empty rule, after the others. */
export const addGroup = (
  draft: CriteriaDraft,
  object: CriteriaSide,
  newKey: () => number
): CriteriaDraft =>
  withSide(draft, object, (side) => ({
    ...side,
    groups: [...side.groups, { key: newKey(), rules: [newRule(newKey)] }],
    listed: true,
  }));

/** Takes a rule away. A group left with no rule goes with it (an empty group would mean every record). */
export const removeRule = (
  draft: CriteriaDraft,
  object: CriteriaSide,
  key: number
): CriteriaDraft =>
  withSide(draft, object, (side) => ({
    groups: side.groups
      .map((group) => ({
        ...group,
        rules: group.rules.filter((rule) => rule.key !== key),
      }))
      .filter((group) => group.rules.length > 0),
    keepOut: side.keepOut.filter((rule) => rule.key !== key),
    listed: true,
  }));

export const removeGroup = (
  draft: CriteriaDraft,
  object: CriteriaSide,
  groupKey: number
): CriteriaDraft =>
  withSide(draft, object, (side) => ({
    ...side,
    groups: side.groups.filter((group) => group.key !== groupKey),
    listed: true,
  }));

export const changeRule = (
  draft: CriteriaDraft,
  object: CriteriaSide,
  key: number,
  patch: Partial<RuleFields>
): CriteriaDraft => {
  const change = (rule: DraftRule) =>
    rule.key === key ? { ...rule, ...patch } : rule;
  return withSide(draft, object, (side) => ({
    ...side,
    groups: side.groups.map((group) => ({
      ...group,
      rules: group.rules.map(change),
    })),
    keepOut: side.keepOut.map(change),
  }));
};

/** Every rule of an object in the order it is drawn: the groups, then Keep out. */
export const rulesOfSide = (side: DraftSide): DraftRule[] => [
  ...side.groups.flatMap((group) => group.rules),
  ...side.keepOut,
];

/**
 * Where the focus goes once something is taken away (6.1): the screen turns a
 * target into the element, so this stays free of the page.
 *  - rule: the field of that rule;
 *  - addRule: the "Add a rule" link of the groups, which an object with no group
 *    left has on its own line;
 *  - keepOutStart: the "Keep some Leads out" link, which comes back when Keep
 *    out has no rule left.
 */
export type FocusTarget =
  | { kind: "rule"; key: number }
  | { kind: "addRule" }
  | { kind: "keepOutStart" };

/** The first rule of the group that took the place of a group that went, else of the one before it. */
const nearestGroupRule = (groups: DraftGroup[], index: number): FocusTarget => {
  const near = groups[index] ?? groups[index - 1];
  return near ? { kind: "rule", key: near.rules[0].key } : { kind: "addRule" };
};

/**
 * The rule that took the place of the one taken away, else the one before it;
 * a group's last rule takes the group with it, so the focus goes to the first
 * field of the group that took its place, else of the one before; Keep out's
 * last rule brings back the link to start it (6.1).
 */
export const focusAfterRemoveRule = (
  draft: CriteriaDraft,
  object: CriteriaSide,
  key: number
): FocusTarget => {
  const side = draft[object];
  const inKeepOut = side.keepOut.findIndex((rule) => rule.key === key);
  if (inKeepOut >= 0) {
    const rest = side.keepOut.filter((rule) => rule.key !== key);
    const near = rest[inKeepOut] ?? rest[inKeepOut - 1];
    return near ? { kind: "rule", key: near.key } : { kind: "keepOutStart" };
  }
  const at = side.groups.findIndex((group) =>
    group.rules.some((rule) => rule.key === key)
  );
  if (at < 0) return { kind: "addRule" };
  const group = side.groups[at];
  if (group.rules.length > 1) {
    const position = group.rules.findIndex((rule) => rule.key === key);
    const rest = group.rules.filter((rule) => rule.key !== key);
    const near = rest[position] ?? rest[position - 1];
    return { kind: "rule", key: near.key };
  }
  return nearestGroupRule(
    side.groups.filter((other) => other.key !== group.key),
    at
  );
};

/** The first field of the group that took the place of the one taken away, else of the one before it. */
export const focusAfterRemoveGroup = (
  draft: CriteriaDraft,
  object: CriteriaSide,
  groupKey: number
): FocusTarget => {
  const groups = draft[object].groups;
  const at = groups.findIndex((group) => group.key === groupKey);
  return nearestGroupRule(
    groups.filter((group) => group.key !== groupKey),
    Math.max(at, 0)
  );
};

/**
 * A limit that is reached, by what would be added. The add link is replaced by
 * a line that says the limit (6.7, "Limits reached"). A group's own limit is
 * said before the limit of the object.
 */
export type LimitCode = "rules" | "groups" | "keepOut" | "total";

export const limitFor = (
  side: DraftSide,
  adding: RuleTarget | "group"
): LimitCode | null => {
  const total = rulesOfSide(side).length;
  if (adding === "group") {
    if (side.groups.length >= LIMITS.groups) return "groups";
    return total >= LIMITS.rulesPerObject ? "total" : null;
  }
  if (adding === "keepOut") {
    if (side.keepOut.length >= LIMITS.keepOut) return "keepOut";
  } else {
    const group = side.groups[adding.group];
    if (group && group.rules.length >= LIMITS.rulesPerGroup) return "rules";
  }
  return total >= LIMITS.rulesPerObject ? "total" : null;
};

// ---------------------------------------------------------------------------
// The fields a rule can use

/** One field of an object, as the stored describe lists it. */
export type FieldInfo = {
  name: string;
  label: string;
  /** Salesforce's own type, lower case: string, picklist, multipicklist, double, date... */
  type: string;
  calculated: boolean;
  picklistValues?: { value: string; label: string; active?: false }[];
};

/**
 * What the screen knows about the fields. Without a describe (the fallback of
 * 6.6) there is none of it: every field is text, and a field cannot be missing
 * from a list there is not.
 */
export type FieldContext = {
  /** A field of an object by its API name, without regard to case; hidden fields too */
  fieldOf: (object: CriteriaSide, name: string) => FieldInfo | undefined;
  /** The stored describe lists this object's fields */
  hasDescribe: (object: CriteriaSide) => boolean;
  /** The fields Salesforce refuses for this object (Sync Health's `criteriaField`) */
  refused: (object: CriteriaSide) => string[];
};

export const NO_FIELDS: FieldContext = {
  fieldOf: () => undefined,
  hasDescribe: () => false,
  refused: () => [],
};

const NUMBER_TYPES = ["int", "long", "double", "currency", "percent"];
export const isNumberType = (type: string): boolean =>
  NUMBER_TYPES.includes(type);
export const isDateType = (type: string): boolean =>
  type === "date" || type === "datetime";

// ---------------------------------------------------------------------------
// Problems and hints (6.7)

/** What blocks Save, by the keys of 6.7. */
export type BlockingCode =
  | "field"
  | "badField"
  | "unknownCondition"
  | "value"
  | "choose"
  | "chooseMany"
  | "number"
  | "numberList"
  | "date"
  | "dateCompare"
  | "booleanValue"
  | "tooLong"
  | "tooMany"
  | "overRules"
  | "overKeepOut"
  | "overTotal"
  | "overGroups";

/** What is only worth a look. */
export type HintCode =
  | "space"
  | "notInList"
  | "multiLegacy"
  | "fieldGone"
  | "fieldUnlisted";

/** Under which part of a rule a note goes. `list` is a note about the group or Keep out the rule opens. */
export type RuleColumn = "field" | "condition" | "value" | "list";

export type NotePlace =
  | { kind: "intro" }
  | { kind: "rule"; key: number; column: RuleColumn };

export type CriteriaNote = {
  object: CriteriaSide;
  place: NotePlace;
  code: BlockingCode | HintCode;
  /** A problem; false for a hint */
  blocking: boolean;
  /** The words of 6.7 */
  text: string;
};

const COPY = PANEL_COPY.criteria;

const PLAIN_COLUMNS: Record<string, RuleColumn> = {
  propertyName: "field",
  condition: "condition",
  propertyValue: "value",
  propertyValues: "value",
};

const COLUMN_ORDER: Record<RuleColumn, number> = {
  field: 0,
  condition: 1,
  value: 2,
  list: 3,
};

/** A thing the check found, before its words and its place on screen are settled. */
type Found = {
  object: CriteriaSide;
  /** The rule it is about; null when it is about the intro */
  rule: DraftRule | null;
  column: RuleColumn;
  code: BlockingCode | HintCode;
  /** What the count of a limit says ("Remove {n}") */
  over?: number;
};

const textOf = (found: Found): string => {
  const { object, rule, code, over = 0 } = found;
  switch (code) {
    case "dateCompare":
      return COPY.errors.dateCompare(rule?.condition ?? "", object);
    case "booleanValue":
      return COPY.errors.booleanValue(object);
    case "overRules":
      return COPY.errors.overRules(over);
    case "overKeepOut":
      return COPY.errors.overKeepOut(over);
    case "overTotal":
      return COPY.errors.overTotal(object, over);
    case "overGroups":
      return COPY.errors.overGroups(over);
    case "fieldGone":
      return COPY.hints.fieldGone(object);
    case "space":
    case "notInList":
    case "multiLegacy":
    case "fieldUnlisted":
      return COPY.hints[code];
    default:
      return COPY.errors[code];
  }
};

/** A thing the check found as something the screen draws. */
const noteOf = (found: Found, blocking: boolean): CriteriaNote => ({
  object: found.object,
  place: found.rule
    ? { kind: "rule", key: found.rule.key, column: found.column }
    : { kind: "intro" },
  code: found.code,
  blocking,
  text: textOf(found),
});

/** The conditions that name values of a picklist, for the hint that a value is not one of its values. */
const NAMING_CONDITIONS = [
  "is",
  "is not",
  "is one of",
  "is not one of",
  "is any of",
  "is not any of",
];

const MULTI_LEGACY_CONDITIONS = [
  "is",
  "is not",
  "contains",
  "does not contain",
  "is one of",
  "is not one of",
];

const hasSpaceAround = (text: string): boolean => text !== text.trim();

/** The text a list condition's value holds, one item at a time. */
const itemsOf = (rule: RuleFields): string[] =>
  takesValueList(rule.condition)
    ? rule.propertyValues
    : rule.propertyValue.split(",").map((item) => item.trim());

/** What a rule is worth a look for, whatever the check of a save says. */
const hintsOf = (
  object: CriteriaSide,
  rule: DraftRule,
  context: FieldContext
): HintCode[] => {
  const hints: HintCode[] = [];
  const info = context.fieldOf(object, rule.propertyName);
  const name = rule.propertyName.toLowerCase();

  const values = takesValueList(rule.condition)
    ? rule.propertyValues
    : takesNoValue(rule.condition)
    ? []
    : [rule.propertyValue];
  if (values.some((value) => value !== "" && hasSpaceAround(value))) {
    hints.push("space");
  }

  if (
    info &&
    (info.type === "picklist" || info.type === "multipicklist") &&
    info.picklistValues &&
    NAMING_CONDITIONS.includes(rule.condition)
  ) {
    const known = new Set(info.picklistValues.map((option) => option.value));
    const chosen = (takesValueList(rule.condition)
      ? rule.propertyValues
      : takesCommaList(rule.condition)
      ? itemsOf(rule)
      : [rule.propertyValue]
    ).filter((value) => value !== "");
    if (chosen.some((value) => !known.has(value))) hints.push("notInList");
  }

  if (
    info?.type === "multipicklist" &&
    MULTI_LEGACY_CONDITIONS.includes(rule.condition)
  ) {
    hints.push("multiLegacy");
  }

  if (name !== "") {
    if (
      context.refused(object).some((refused) => refused.toLowerCase() === name)
    ) {
      hints.push("fieldGone");
    } else if (context.hasDescribe(object) && !info) {
      hints.push("fieldUnlisted");
    }
  }
  return hints;
};

/**
 * What the screen finds wrong with a rule that the save check (`validateForSave`)
 * cannot see, because it needs the type of the field: a number that is not one,
 * a date compared with is more than, a checkbox given something else.
 */
const typedProblemsOf = (
  rule: DraftRule,
  info: FieldInfo | undefined
): { column: RuleColumn; code: BlockingCode }[] => {
  const found: { column: RuleColumn; code: BlockingCode }[] = [];
  const { condition, propertyValue } = rule;
  // a value of nothing but spaces is no value, as it never was on this screen
  // (the save check only calls an empty text empty)
  if (
    takesValue(condition) &&
    !takesValueList(condition) &&
    !takesDate(condition) &&
    propertyValue.trim() === ""
  ) {
    found.push({ column: "value", code: emptyValueCode(rule, info) });
  }
  if (!info) return found;
  if (isNumberType(info.type)) {
    if (takesCommaList(condition)) {
      if (
        propertyValue.trim() !== "" &&
        !propertyValue
          .split(",")
          .every((item) => isFiniteNumberText(item.trim()))
      ) {
        found.push({ column: "value", code: "numberList" });
      }
    } else if (
      (condition === "is" ||
        condition === "is not" ||
        takesNumber(condition)) &&
      propertyValue.trim() !== "" &&
      !isFiniteNumberText(propertyValue)
    ) {
      found.push({ column: "value", code: "number" });
    }
  }
  if (isDateType(info.type) && takesNumber(condition)) {
    found.push({ column: "condition", code: "dateCompare" });
  }
  // no value at all is said by the save check, and a checkbox asks to choose
  // (emptyValueCode); a value that is neither true nor false is said here
  if (
    info.type === "boolean" &&
    !takesNoValue(condition) &&
    propertyValue !== "" &&
    propertyValue !== "true" &&
    propertyValue !== "false"
  ) {
    found.push({ column: "value", code: "booleanValue" });
  }
  return found;
};

/** What "no value" is called for a field: a picklist asks to choose, anything else to add. */
const emptyValueCode = (
  rule: RuleFields,
  info: FieldInfo | undefined
): BlockingCode => {
  if (info?.type === "picklist") {
    return takesCommaList(rule.condition) || takesValueList(rule.condition)
      ? "chooseMany"
      : "choose";
  }
  if (info?.type === "boolean") return "choose";
  return "value";
};

const FLAT_RULE = /^filterProperties\[(\d+)\](?:\.(\w+))?$/;
const GROUP_RULE = /^rules\.include\.conditions\[(\d+)\]\.conditions\[(\d+)\](?:\.(\w+))?$/;
const GROUP_LIST = /^rules\.include\.conditions\[(\d+)\]\.conditions$/;
const KEEP_RULE = /^rules\.exclude\.conditions\[(\d+)\](?:\.(\w+))?$/;

/** Where in the draft a path of the save check points: a rule (with the part of it), a group's list, Keep out's list, or the object. */
type Located =
  | { kind: "rule"; rule: DraftRule; part: string | undefined }
  | { kind: "groupList"; group: DraftGroup | undefined }
  | { kind: "keepOutList" }
  | { kind: "side" };

const locate = (path: string, side: DraftSide): Located | null => {
  let match = FLAT_RULE.exec(path);
  if (match) {
    const rule = side.groups[0]?.rules[Number(match[1])];
    return rule ? { kind: "rule", rule, part: match[2] } : null;
  }
  match = GROUP_RULE.exec(path);
  if (match) {
    const rule = side.groups[Number(match[1])]?.rules[Number(match[2])];
    return rule ? { kind: "rule", rule, part: match[3] } : null;
  }
  match = KEEP_RULE.exec(path);
  if (match) {
    const rule = side.keepOut[Number(match[1])];
    return rule ? { kind: "rule", rule, part: match[2] } : null;
  }
  if (path === "filterProperties")
    return { kind: "groupList", group: side.groups[0] };
  match = GROUP_LIST.exec(path);
  if (match) return { kind: "groupList", group: side.groups[Number(match[1])] };
  if (path === "rules.exclude.conditions") return { kind: "keepOutList" };
  if (path === "rules" || path === "rules.include.conditions") {
    return { kind: "side" };
  }
  return null;
};

/**
 * One problem of the save check as something the screen draws, or null when
 * the screen has no words for it (a shape the screen cannot make: the server's
 * own sentence says it).
 */
const foundFromProblem = (
  object: CriteriaSide,
  side: DraftSide,
  problem: { path: string; problem: string },
  context: FieldContext
): Found | null => {
  const at = locate(problem.path, side);
  if (!at) return null;
  const code = problem.problem;
  if (at.kind === "rule") {
    const column = at.part ? PLAIN_COLUMNS[at.part] : undefined;
    if (!column || column === "list") return null;
    const info = context.fieldOf(object, at.rule.propertyName);
    switch (code) {
      case "number":
        // a date compared with is more than: the date is not the trouble, the
        // condition is, and the screen says that (dateCompare)
        return info && isDateType(info.type)
          ? null
          : { object, rule: at.rule, column, code };
      case "field":
      case "badField":
      case "unknownCondition":
      case "chooseMany":
      case "date":
      case "tooLong":
      case "tooMany":
        return { object, rule: at.rule, column, code };
      case "value":
        return {
          object,
          rule: at.rule,
          column,
          code: emptyValueCode(at.rule, info),
        };
      default:
        return null;
    }
  }
  const total = rulesOfSide(side).length;
  if (code === "overRules" && at.kind === "groupList" && at.group) {
    const first = at.group.rules[0];
    return first
      ? {
          object,
          rule: first,
          column: "list",
          code,
          over: at.group.rules.length - LIMITS.rulesPerGroup,
        }
      : null;
  }
  if (code === "overKeepOut" && at.kind === "keepOutList") {
    const first = side.keepOut[0];
    return first
      ? {
          object,
          rule: first,
          column: "list",
          code,
          over: side.keepOut.length - LIMITS.keepOut,
        }
      : null;
  }
  if (code === "overTotal") {
    return {
      object,
      rule: null,
      column: "list",
      code,
      over: total - LIMITS.rulesPerObject,
    };
  }
  if (code === "overGroups") {
    return {
      object,
      rule: null,
      column: "list",
      code,
      over: side.groups.length - LIMITS.groups,
    };
  }
  return null;
};

export type CriteriaNotes = {
  /** What to draw, in the order of the screen: the notes the customer can see now */
  notes: CriteriaNote[];
  /**
   * What stops a Save, in screen order, whether it shows yet or not: the
   * problems of an object whose rules were changed. The first is where the
   * focus goes.
   */
  blockers: CriteriaNote[];
  /**
   * Every problem of every object, changed or not, in screen order. Check a
   * record needs the rules it sends to be sound, whatever was edited, because
   * the server checks all of them (6.9).
   */
  problems: CriteriaNote[];
};

export type NotesInput = {
  draft: CriteriaDraft;
  /** The draft the screen opened with */
  baseline: CriteriaDraft;
  /** The value the baseline was read from (see criteriaToSave) */
  base: unknown;
  context?: FieldContext;
  /** Save was pressed: every problem shows, and updates as the customer edits */
  tried?: boolean;
};

/**
 * Everything the screen says about the rules on it (6.7):
 *  - the problems the save check finds (`validateForSave` with every object
 *    counting as changed, so the stored value is checked as well as the edit),
 *    in the words of 6.7, and the ones that need the type of a field;
 *  - hints, which never block and never wait for Save.
 * A problem of a rule that is still as stored shows from the moment the screen
 * opens; a problem of a rule the customer edited shows once Save was pressed
 * (`tried`). A problem about a group, Keep out or the object is about counts
 * the customer can only change by taking rules away, so it always shows. It
 * blocks Save only when that object's rules were changed, because the server
 * checks only what a save changes (2.4): a Contacts list over a limit never stops
 * a Leads save.
 */
export const criteriaNotes = ({
  draft,
  baseline,
  base,
  context = NO_FIELDS,
  tried = false,
}: NotesInput): CriteriaNotes => {
  const value = criteriaToSave(draft, base);
  const problems = validateForSave(value, { stored: undefined });
  const blocking: Found[] = [];
  const hints: Found[] = [];
  const changed: Record<CriteriaSide, boolean> = {
    Lead: sideChanged(draft, baseline, "Lead"),
    Contact: sideChanged(draft, baseline, "Contact"),
    Opportunity: sideChanged(draft, baseline, OPPORTUNITY_SIDE),
    Campaign: sideChanged(draft, baseline, CAMPAIGN_SIDE),
  };

  ALL_SIDES.forEach((object) => {
    const side = draft[object];
    // the screen's checks come first: where both find something in the same
    // part of a rule, the one with the field's type says it better
    rulesOfSide(side).forEach((rule) => {
      typedProblemsOf(
        rule,
        context.fieldOf(object, rule.propertyName)
      ).forEach(({ column, code }) =>
        blocking.push({ object, rule, column, code })
      );
      hintsOf(object, rule, context).forEach((code) =>
        hints.push({
          object,
          rule,
          column:
            code === "space" || code === "notInList"
              ? "value"
              : code === "multiLegacy"
              ? "condition"
              : "field",
          code,
        })
      );
    });
    problems
      .filter((problem: SaveProblem) => problem.object === object)
      .forEach((problem: SaveProblem) => {
        const found = foundFromProblem(object, side, problem, context);
        if (found) blocking.push(found);
      });
  });

  const seen = new Set<string>();
  const keyOf = (note: CriteriaNote) =>
    `${note.object}:${
      note.place.kind === "rule"
        ? `${note.place.key}:${note.place.column}`
        : "intro"
    }:${note.blocking ? "problem" : note.code}`;

  const position = (note: CriteriaNote): number[] => {
    const side = draft[note.object];
    const order = rulesOfSide(side);
    const at =
      note.place.kind === "rule"
        ? order.findIndex(
            (rule) => note.place.kind === "rule" && rule.key === note.place.key
          )
        : -1;
    return [
      ALL_SIDES.indexOf(note.object),
      at,
      note.place.kind === "rule" ? COLUMN_ORDER[note.place.column] : -1,
      note.blocking ? 0 : 1,
    ];
  };
  const byScreenOrder = (a: CriteriaNote, b: CriteriaNote) => {
    const left = position(a);
    const right = position(b);
    for (let i = 0; i < left.length; i += 1) {
      if (left[i] !== right[i]) return left[i] - right[i];
    }
    return 0;
  };

  // one problem to a part of a rule, the first found
  const problemNotes = blocking
    .map((found) => noteOf(found, true))
    .filter((note) => {
      const key = keyOf(note);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort(byScreenOrder);
  const hintNotes = hints.map((found) => noteOf(found, false));

  const untouched = new Set(
    ALL_SIDES.flatMap((object) =>
      rulesOfSide(draft[object])
        .filter(isUntouched)
        .map((rule) => rule.key)
    )
  );
  const visible = problemNotes.filter(
    (note) =>
      tried ||
      note.place.kind === "intro" ||
      note.place.column === "list" ||
      untouched.has(note.place.key)
  );
  return {
    notes: [...visible, ...hintNotes].sort(byScreenOrder),
    blockers: problemNotes.filter((note) => changed[note.object]),
    problems: problemNotes,
  };
};

/** The notes of one rule, in the order of its parts. */
export const notesOfRule = (
  notes: CriteriaNote[],
  object: CriteriaSide,
  key: number
): CriteriaNote[] =>
  notes.filter(
    (note) =>
      note.object === object &&
      note.place.kind === "rule" &&
      note.place.key === key
  );

/** The problems about an object as a whole: more groups or rules than the limits allow. */
export const introNotes = (
  notes: CriteriaNote[],
  object: CriteriaSide
): CriteriaNote[] =>
  notes.filter((note) => note.object === object && note.place.kind === "intro");

/** The part of a rule that takes the focus when Save finds it wrong. */
export type NoteFocus = {
  key: number;
  column: "field" | "condition" | "value";
};

/**
 * Where the focus goes for a problem (6.7: the first one in screen order): the
 * part of the rule it is about. A problem about the group or Keep out a rule
 * opens goes to that rule's field, and one about the object as a whole (more
 * groups or rules than the limits allow) to the first field of the card, the
 * place to start taking rules away.
 */
export const noteFocusOf = (
  note: CriteriaNote,
  draft: CriteriaDraft
): NoteFocus | null => {
  if (note.place.kind === "rule") {
    const { column } = note.place;
    return {
      key: note.place.key,
      column: column === "condition" || column === "value" ? column : "field",
    };
  }
  const first = rulesOfSide(draft[note.object])[0];
  return first ? { key: first.key, column: "field" } : null;
};

/**
 * The notes for the problems the server found in a save it refused (400
 * `salesforce_sync_criteria_invalid`), on the rules they point at, as the
 * screen marks them on load. The paths are those of the value that was sent,
 * which is `criteriaToSave` of the draft that was on screen.
 */
export const notesFromServer = (
  problems: SalesforceCriteriaProblem[],
  draft: CriteriaDraft,
  context: FieldContext = NO_FIELDS
): CriteriaNote[] =>
  problems.flatMap((problem) => {
    if (!problem.object) return [];
    const found = foundFromProblem(
      problem.object,
      draft[problem.object],
      problem,
      context
    );
    return found ? [noteOf(found, true)] : [];
  });

// ---------------------------------------------------------------------------
// When a save does not go through (7.8)

/**
 * What the screen does with a refused or failed save:
 *  - conflict: 409 `salesforce_sync_criteria_conflict`, someone saved other
 *    rules; the review closes and the screen shows `message` with a button that
 *    reloads the saved rules. The draft stays;
 *  - invalid: 400 `salesforce_sync_criteria_invalid`, the check refused the
 *    rules; `message` is the server's sentence and `problems` are marked on
 *    their rules;
 *  - refused: any other answer that has a message, for example 409
 *    `salesforce_v2_not_enabled`; the review closes and `message` shows;
 *  - retry: no answer (the network failed) or a 5xx; the review stays open and
 *    says "We could not save. Your changes are still here. Try again."
 */
export type SaveFailure =
  | { kind: "conflict"; message: string }
  | {
      kind: "invalid";
      message: string;
      problems: SalesforceCriteriaProblem[];
    }
  | { kind: "refused"; message: string }
  | { kind: "retry"; message: string };

export const saveFailureOf = (
  error: SalesforceCriteriaRequestError,
  nowMs: number = Date.now()
): SaveFailure => {
  const { status, errorCode } = error;
  if (status === undefined || status >= 500) {
    return { kind: "retry", message: PANEL_COPY.footer.saveFailed };
  }
  if (status === 409 && errorCode === CRITERIA_CONFLICT_CODE) {
    return {
      kind: "conflict",
      message: COPY.conflict(
        error.changedBy ?? null,
        formatWhen(error.changedAt, nowMs)
      ),
    };
  }
  if (errorCode === CRITERIA_INVALID_CODE) {
    return {
      kind: "invalid",
      message: error.message,
      problems: error.problems ?? [],
    };
  }
  return { kind: "refused", message: error.message };
};

// ---------------------------------------------------------------------------
// The overview

/** What one object's stored rules come to, for the overview's row (6.8): groups, the rules of the first, Keep out rules. */
export const summaryOf = (
  stored: unknown,
  object: CriteriaSide
): { groups: number; rules: number; keepOut: number } => {
  const reading =
    object === OPPORTUNITY_SIDE
      ? readOpportunitySide(stored)
      : object === CAMPAIGN_SIDE
      ? campaignReadingOf(campaignCriteriaOfStored(stored))
      : readSide(stored, object);
  return {
    groups: reading.groups.length,
    rules: reading.groups[0]?.length ?? 0,
    keepOut: reading.keepOut.length,
  };
};
